import React, { createContext, useContext, useEffect, useState } from 'react';
import { loginWithToken } from '../services/api';

const AuthContext = createContext(null);

const TOKEN_KEY = 'fpr_token';
const LEGACY_TOKEN_KEY = 'fpr_token';

/**
 * Read the active wizard JWT from sessionStorage (preferred) or migrate a
 * legacy localStorage token into the current tab's sessionStorage.
 *
 * sessionStorage is tab-scoped, which prevents one magic-link login from
 * silently rewriting another open tab's auth identity.
 */
function readStoredToken() {
  const sessionToken = sessionStorage.getItem(TOKEN_KEY);
  if (sessionToken) {
    return sessionToken;
  }

  // One-time migration for tabs that still hold a pre-fix localStorage token.
  const legacyToken = localStorage.getItem(LEGACY_TOKEN_KEY);
  if (legacyToken) {
    sessionStorage.setItem(TOKEN_KEY, legacyToken);
    localStorage.removeItem(LEGACY_TOKEN_KEY);
    return legacyToken;
  }

  return null;
}

function clearStoredToken() {
  sessionStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(LEGACY_TOKEN_KEY);
}

function writeStoredToken(token) {
  sessionStorage.setItem(TOKEN_KEY, token);
  // Ensure no shared localStorage identity remains for other tabs.
  localStorage.removeItem(LEGACY_TOKEN_KEY);
}

/**
 * Decode a JWT payload without verifying the signature (client-side only).
 * @param {string} token
 * @returns {object}
 */
function decodeJwtPayload(token) {
  const [, payload] = token.split('.');
  const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
  const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '=');
  return JSON.parse(atob(padded));
}

/**
 * Provides FPR client authentication state throughout the app.
 *
 * Tokens are stored in sessionStorage under the key `fpr_token` so each
 * browser tab keeps an independent client identity.
 */
export function AuthProvider({ children }) {
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const stored = readStoredToken();
    if (!stored) {
      setLoading(false);
      return;
    }
    try {
      const decoded = decodeJwtPayload(stored);
      if (decoded.exp && Date.now() / 1000 > decoded.exp) {
        clearStoredToken();
        setLoading(false);
        return;
      }
      setClient({ client_id: decoded.sub, name: decoded.name || '' });
    } catch {
      clearStoredToken();
    }
    setLoading(false);
  }, []);

  /**
   * Log in using a raw magic-link token.
   * @param {string} rawToken
   */
  async function login(rawToken) {
    setError(null);
    try {
      const data = await loginWithToken(rawToken);
      writeStoredToken(data.access_token);
      setClient({ client_id: data.client_id, name: data.name });
      return data;
    } catch (err) {
      // Surface the real failure mode so we don't always blame "expired link".
      const detail = err.response?.data?.detail;
      const status = err.response?.status;
      let msg;
      if (detail) {
        msg = detail;
      } else if (status) {
        msg = `Login failed (HTTP ${status}). Please contact your advisor.`;
      } else {
        // No response at all → network/CORS/mixed-content failure.
        msg = `Could not reach the login service (${err.message || 'network error'}). Please contact your advisor.`;
      }
      setError(msg);
      throw new Error(msg);
    }
  }

  function logout() {
    clearStoredToken();
    setClient(null);
  }

  return (
    <AuthContext.Provider value={{ client, loading, error, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
